import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Grid,
  Typography,
  CircularProgress,
  Box,
  useTheme
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  ReferenceLine,
  TooltipProps
} from 'recharts';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Tooltip as MuiTooltip } from '@mui/material';
import '../NewDashboard.css';
import { RootStateType } from 'src/store/RootReducer';
import { getVarianceReport } from 'src/services/timeManagementService';
import { AppActions } from 'src/store/AppReducer';
import { toast } from 'react-toastify';
import {  format } from 'date-fns';


const CircularProgressWithLabel = ({ completionPercentage }) => {
  const colors = ['#2196F3', '#F44336', '#FF9800', '#4CAF50'];

  const fullRings = Math.floor(completionPercentage / 100);
  const currentRingProgress = completionPercentage % 100;

  // Ensure at least one ring is displayed, even at 0%
  const totalRings = Math.max(
    Math.min(fullRings + (currentRingProgress > 0 ? 1 : 0), colors.length),
    1 // Minimum of 1 ring
  );

  const displayPercentage = Math.min(
    Math.round(completionPercentage),
    totalRings * 100
  );

  const baseSize = 100;
  const ringThickness = 4;
  const gapBetweenRings = 12;

  const outerSize =
    baseSize + (totalRings - 1) * 2 * (ringThickness + gapBetweenRings);

  return (
    <Box
      position="relative"
      width={outerSize}
      height={outerSize}
      display="inline-flex"
      justifyContent="center"
      alignItems="center"
    >
      {Array.from({ length: totalRings }).map((_, index) => {
        const isCurrentRing = index === fullRings;
        const isZeroProgress = completionPercentage === 0 && index === 0;
        const ringSize =
          baseSize + index * 2 * (ringThickness + gapBetweenRings);
        const offset = (outerSize - ringSize) / 2;

        return (
          <React.Fragment key={index}>
            {/* Background Shade for All Rings (including at 0%) */}
            <CircularProgress
              variant="determinate"
              value={100}
              size={ringSize}
              thickness={ringThickness}
              sx={{
                color: `${colors[index % colors.length]}33`,
                position: 'absolute',
                top: offset,
                left: offset,
                svg: { strokeLinecap: 'round' },
                filter: 'blur(2px)'
              }}
            />

            {/* Actual Progress Ring */}
            <CircularProgress
              variant="determinate"
              value={
                isZeroProgress ? 0 : isCurrentRing ? currentRingProgress : 100
              }
              size={ringSize}
              thickness={ringThickness}
              sx={{
                color: colors[index % colors.length],
                position: 'absolute',
                top: offset,
                left: offset,
                svg: { strokeLinecap: 'round' },
                transition: '0.4s ease-in-out',
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
              }}
            />
          </React.Fragment>
        );
      })}

      <Box
        position="absolute"
        top="50%"
        left="50%"
        sx={{
          transform: 'translate(-50%, -50%)',
          textAlign: 'center'
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: '1.4rem'
          }}
        >
          {displayPercentage}%
        </Typography>
      </Box>
    </Box>
  );
};
const TimeManagementWidget = () => {
  const dispatch = useDispatch();
  const empId = useSelector(
    (state: RootStateType) => state.appReducer?.user?.EmployeeId
  );
  const [weekRange, setWeekRange] = useState('');
  const [enteredHours, setEnteredHours] = useState(0);
  const [remainingHours, setRemainingHours] = useState(0);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [timeTrackingData, setTimeTrackingData] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0); // 0 = current week
  const navigate = useNavigate();
  const theme = useTheme();

  // Accept offset for week navigation
  const getWeekRange = (offset = 0) => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const startDate = new Date(today);
    startDate.setDate(
      today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1) + offset * 7
    );
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);

    const formatDate = (date) =>
      new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }).format(date);

    return {
      range: `${formatDate(startDate)} - ${formatDate(endDate)}`,
      startDate,
      endDate,
      daysInWeek: 7
    };
  };

  const parseTimeToHours = (timeString) => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(/[h|m|:]+/).map(Number);
    return hours + (minutes || 0) / 60;
  };

  const formatHoursToHm = (decimalHours) => {
    if (decimalHours === null || decimalHours === undefined) {
      return '0h 0m';
    }
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    return `${hours}h ${minutes}m`;
  };

  const parseTimeToMinutes = (timeString) => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(/[h|m|:]+/).map(Number);
    return hours * 60 + (minutes || 0);
  };

  const formatMinutesToHm = (totalMinutes) => {
    const sign = totalMinutes < 0 ? '-' : '';
    const absMinutes = Math.abs(totalMinutes);
    const hours = Math.floor(absMinutes / 60);
    const minutes = absMinutes % 60;
    return `${sign}${hours}h ${minutes}m`;
  };


  const fetchTimeTrackingData = async () => {
    const { startDate, endDate } = getWeekRange(weekOffset);
    const requestData = {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      employeeIds: [empId],
      projectIds: [],
      customerIds: []
    };

    try {
      const response = await getVarianceReport(requestData, 1, 10);
      const employeeData = response.data.data.employeeHours[0];
      if (employeeData) {
        const expectedMinutes = parseTimeToMinutes(employeeData.expectedHours);
        const enteredMinutes = parseTimeToMinutes(employeeData.enteredHours);
        setEnteredHours(enteredMinutes / 60);
        setRemainingHours(expectedMinutes - enteredMinutes);
        setCompletionPercentage((enteredMinutes / expectedMinutes) * 100);
      } else {
        setEnteredHours(0);
        setRemainingHours(0);
        setCompletionPercentage(0);
      }
    } catch (error) {
      toast.error('Error fetching time tracking data. Please try again later.');
      setEnteredHours(0);
      setRemainingHours(0);
      setCompletionPercentage(0);
    }
  };

  const fetchDailyData = async () => {
    dispatch(AppActions.showLoader());
    const { startDate, daysInWeek } = getWeekRange(weekOffset);

    const days = Array.from({ length: daysInWeek }, (_, i) => {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      return date;
    });

    const requests = days.map(async (date) => {
      const requestData = {
        startDate: date.toISOString().split('T')[0],
        endDate: date.toISOString().split('T')[0],
        employeeIds: [empId],
        projectIds: [],
        customerIds: []
      };

      try {
        const response = await getVarianceReport(requestData, 1, 10);
        const employeeData = response.data.data.employeeHours[0];
        return {
          date,
          day: date.toLocaleDateString('en-US', { weekday: 'short' }),
          expectedHours: employeeData
            ? parseTimeToHours(employeeData.expectedHours)
            : 0,
          enteredHours: employeeData
            ? parseTimeToHours(employeeData.enteredHours)
            : 0
        };
      } catch (error) {
        toast.error(`Error fetching data for ${date.toLocaleDateString()}`);
        return {
          date,
          day: date.toLocaleDateString('en-US', { weekday: 'short' }),
          expectedHours: 0,
          enteredHours: 0
        };
      }
    });

    try {
      const results = await Promise.all(requests);

      const filteredResults = results.filter((day) => {
        const isWeekend = day.date.getDay() === 0 || day.date.getDay() === 6;
        return !isWeekend || day.enteredHours > 0 || day.expectedHours > 0;
      });
      const actualBurndownData = calculateActualBurndown(filteredResults);
      setTimeTrackingData(actualBurndownData);
    } catch (error) {
      toast.error('Error processing daily data');
      setTimeTrackingData([]);
    } finally {
      dispatch(AppActions.hideLoader());
    }
  };


  const calculateActualBurndown = (dailyData) => {
    // Filter out non-working days (expectedHours > 0)
    const validWorkingDays = dailyData.filter((day) => day.expectedHours > 0);
    const totalExpected = validWorkingDays.reduce(
      (sum, day) => sum + day.expectedHours,
      0
    );

    let remainingHours = totalExpected;
    let cumulativeEntered = 0;

    // Map date string to expectedHours for quick lookup
    const workingDayMap = {};
    validWorkingDays.forEach((day) => {
      // Use date string in 'YYYY-MM-DD' format
      workingDayMap[day.date.toISOString().split('T')[0]] = day.expectedHours;
    });

    // Initialize result with the starting point
    const burndownData = [
      {
        day: '',
        label: '',
        remaining: totalExpected,
        entered: 0,
        ideal: totalExpected
      }
    ];

    let idealRemaining = totalExpected;

    dailyData.forEach((day) => {
      cumulativeEntered += day.enteredHours;
      remainingHours = totalExpected - cumulativeEntered;

      const dateStr = day.date.toISOString().split('T')[0];
      if (workingDayMap[dateStr] !== undefined) {
        idealRemaining -= workingDayMap[dateStr];
        if (idealRemaining < 0) idealRemaining = 0;
      }

      burndownData.push({
        day: day.day,
        label: `${day.day}\n${format(day.date, 'dd-MMM')}`,
        remaining: remainingHours,
        entered: cumulativeEntered,
        ideal: idealRemaining
      });
    });

    return burndownData;
  };

  const handleNavigate = () => {
    navigate('/time-entry');
  };

  const CustomTooltip: React.FC<TooltipProps<any, any>> = ({
    active,
    payload,
    label
  }) => {
    if (!active || !payload || !payload.length) return null;

    const dayData = payload[0].payload;

    return (
      <Box
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          border: '1px solid #ddd',
          borderRadius: '4px',
          padding: '8px',
          fontWeight: 500,
          fontSize: '12px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
        }}
      >
        <div>
          <strong>Day: {label}</strong>
        </div>
        {dayData.entered !== undefined && (
          <div>Actual Effort: {formatHoursToHm(dayData.entered)}</div>
        )}
        {dayData.remaining !== undefined && (
          <div>Remaining Effort: {formatMinutesToHm(Math.round(dayData.remaining * 60))}</div>
        )}
        {dayData.ideal !== undefined && (
          <div>Ideal Effort: {formatHoursToHm(dayData.ideal)}</div>
        )}
      </Box>
    );
  };
  const CustomXAxisTick = (props) => {
    const { x, y, payload } = props;
    const [day, date] = (payload.value || '').split('\n');
    const isMobile = window.innerWidth < 600;
    const dayFontSize = isMobile ? 10 : 11;
    const dateFontSize = isMobile ? 9 : 10;
    const dateYOffset = isMobile ? 10 : 12;

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={7}
          textAnchor="middle"
          fill="#A0A0A0"
          fontSize={dayFontSize}
          fontWeight={500}
        >
          {day}
        </text>
        <text
          x={0}
          y={dateYOffset}
          dy={7}
          textAnchor="middle"
          fill="#666"
          fontSize={dateFontSize}
          fontWeight={400}
        >
          {date}
        </text>
      </g>
    );
  };

  const getEvenTicks = (min: number, max: number) => {
    let start = Math.floor(min / 2) * 2;
    let end = Math.ceil(max / 2) * 2;

    let range = end - start;
    let step = 2;
    if (range > 16) step = 4;
    if (range > 40) step = 8;

    if (start < 0 && end > 0) {
      start = Math.min(start, 0);
      end = Math.max(end, 0);
    }

    const ticks = [];
    for (let i = start; i <= end; i += step) {
      ticks.push(i);
    }
    if (!ticks.includes(0) && start < 0 && end > 0) {
      ticks.push(0);
      ticks.sort((a, b) => a - b);
    }
    return ticks;
  };

  const yValues = timeTrackingData.flatMap((d) => [d.remaining, d.ideal]);
  const yMin = Math.min(...yValues, 0);
  const yMax = Math.max(...yValues, 0);
  const evenTicks = getEvenTicks(yMin, yMax);

  useEffect(() => {
    const { range } = getWeekRange(weekOffset);
    setWeekRange(range);
    fetchTimeTrackingData();
    fetchDailyData();
    // eslint-disable-next-line
  }, [empId, weekOffset]);

  return (
    <Card
      sx={{
        p: { xs: '16px', sm: '24px' },
        borderRadius: '6px',
        width: '100%',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap'
        }}
      >
        <div>
          <Typography
            variant="h6"
            fontWeight="bold"
            display="flex"
            alignItems="center"
            gap={1}
          >
            <HourglassEmptyIcon sx={{ fontSize: 18, color: '#064F8C'}} />
            Time Management{' '}
            <span style={{ fontSize: '12px', color: '#666', fontWeight: 400 }}>
              {weekOffset === 0
                ? '(Current week)'
                : weekOffset < 0
                ? `(${Math.abs(weekOffset)} week${
                    Math.abs(weekOffset) > 1 ? 's' : ''
                  } ago)`
                : `(${weekOffset} week${weekOffset > 1 ? 's' : ''} ahead)`}
            </span>
          </Typography>
        </div>
        <div>
          <Typography
            variant="body2"
            style={{
              marginTop: '8px',
              fontSize: '12px',
              cursor: 'pointer',
              color: '#26aff5'
            }}
            onClick={handleNavigate}
          >
            My current tasks →
          </Typography>
        </div>
      </div>
      {/* Week navigation controls */}
      
      <Box
  sx={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    mb: 1,
    gap: 0.5
  }}
>
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1
    }}
  >
    <MuiTooltip title="Previous week" arrow>
      <Box
        component="span"
        sx={{
          cursor: 'pointer',
          borderRadius: '50%',
          p: 0.5,
          transition: 'background 0.2s',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover': { background: '#e3f2fd' }
        }}
        onClick={() => setWeekOffset((prev) => prev - 1)}
      >
        <ArrowBackIosNewIcon fontSize="small" />
      </Box>
    </MuiTooltip>

    <Typography
      variant="caption"
      color="textSecondary"
      sx={{
        mx: 0.5,
        display: 'flex',
        alignItems: 'center'
      }}
    >
      {weekRange}
    </Typography>

    <MuiTooltip
      title={
        weekOffset === 0 ? 'You are on the current week' : 'Next week'
      }
      arrow
    >
      <Box
        component="span"
        sx={{
          cursor: weekOffset === 0 ? 'not-allowed' : 'pointer',
          borderRadius: '50%',
          p: 0.5,
          transition: 'background 0.2s',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: weekOffset === 0 ? '#ccc' : 'inherit',
          '&:hover': weekOffset !== 0 ? { background: '#e3f2fd' } : {}
        }}
        onClick={() =>
          weekOffset !== 0 && setWeekOffset((prev) => prev + 1)
        }
      >
        <ArrowForwardIosIcon fontSize="small" />
      </Box>
    </MuiTooltip>
  </Box>
</Box>

      <Grid container spacing={3}>
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            borderBottom: { xs: '1px solid #eee', md: 'none' },
            pb: { xs: 2, md: 0 }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%'
            }}
          >
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ textAlign: 'center', mb: 1 }}
            >
              Remaining Effort
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ textAlign: 'center', mb: 3 }}
            >
              {formatMinutesToHm(remainingHours)}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                maxWidth: 200,
                mx: 'auto'
              }}
            >
              <CircularProgressWithLabel
                completionPercentage={completionPercentage}
              />
            </Box>

            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ textAlign: 'center', mt: 3, mb: 1 }}
            >
              Actual Effort
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ textAlign: 'center' }}
            >
              {formatHoursToHm(enteredHours)}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={7}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              height: '100%'
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: '14px',
                color: '#666',
                fontWeight: 300,
                mb: 1,
                alignSelf: 'center',
                width: '100%',
                textAlign: 'center'
              }}
            >
              Burndown Chart
            </Typography>

            <Box
              sx={{
                width: '100%',
                height: { xs: 300, sm: 400 },
                mt: 1
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={timeTrackingData}
                  margin={{ top: 5, right: 20, left: 10, bottom: 20 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f0f0f0"
                    vertical={true}
                  />

                  <ReferenceLine
                    y={0}
                    stroke="Black"
                    strokeWidth={2}
                    strokeDasharray="6 3"
                  />
                  <YAxis
                    stroke="#A0A0A0"
                    domain={[Math.floor(yMin), Math.ceil(yMax)]}
                    tickCount={12}
                    ticks={evenTicks}
                    tickFormatter={(value) => Math.round(value).toString()}
                    label={{
                      value: 'Hours',
                      angle: -90,
                      position: 'insideLeft',
                      offset: -3,
                      style: {
                        textAnchor: 'middle',
                        fontSize: '12px',
                        fill:  theme.palette.text.primary,
                        fontWeight: 600
                      }
                    }}
                    tick={{ fontSize: '12px', fill: '#A0A0A0' }}
                    width={30}
                  />

                  <XAxis
                    dataKey="label"
                    stroke="#A0A0A0"
                    tick={<CustomXAxisTick />}
                    interval={0}
                    label={{
                      value: 'Days',
                      position: 'insideBottom',
                      offset: -15,
                      style: {
                        textAnchor: 'middle',
                        fontSize: '12px',
                        fill:  theme.palette.text.primary,
                        fontWeight: 600,
                      }
                    }}
                    height={30}
                  />

                  <Tooltip content={<CustomTooltip />} />

                  <Legend
                    verticalAlign="top"
                    height={30}
                    wrapperStyle={{
                      paddingBottom: '5px'
                    }}
                    iconType="circle"
                    iconSize={10}
                    formatter={(value, entry) => (
                      <span
                        style={{
                          color: '#666',
                          fontSize: '12px',
                          marginLeft: '5px'
                        }}
                      >
                        {value}
                      </span>
                    )}
                  />
                  <Line
                    type="monotone"
                    dataKey="remaining"
                    name="Remaining Effort"
                    stroke="#064F8C"
                    strokeWidth={2}
                    dot={{
                      r: 3,
                      strokeWidth: 2,
                      fill: '#fff',
                      stroke: '#064F8C'
                    }}
                    activeDot={{
                      r: 5,
                      strokeWidth: 2,
                      fill: '#fff',
                      stroke: '#064F8C'
                    }}
                  />

                  <Line
                    type="monotone"
                    dataKey="ideal"
                    name="Ideal Effort"
                    stroke="#FF6B6B"
                    strokeWidth={2}
                    activeDot={{
                      r: 5,
                      strokeWidth: 2,
                      fill: '#fff',
                      stroke: '#FF6B6B'
                    }}
                    dot={{
                      r: 3,
                      strokeWidth: 1,
                      fill: '#fff',
                      stroke: '#FF6B6B'
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
};

export default TimeManagementWidget;
